import { defineStore } from 'pinia'
import { shallowRef, ref, computed } from 'vue'
import { scoreApi, currentScope } from '../utils/scoreSvcApiNew.js'
import { isEarlyWarningScore } from '../utils/scoreThresholds.js'

const PAGE_SIZE = 100
const DETAIL_PAGE_SIZE = 50

export const useScoreStore = defineStore('score', () => {
  const results = shallowRef([])
  const summary = shallowRef(null)
  const loading = ref(false)
  const loadingMore = ref(false)
  const error = ref(null)
  const selectedCustomerId = ref(null)

  // Clear any stale scoreActiveScope that may cause 403 due to JS precision loss on large Long IDs
  localStorage.removeItem('scoreActiveScope')

  // Pagination state
  const currentPage = ref(0)
  const totalCount = ref(0)
  const hasMore = ref(false)

  // Overdue/warning dashboard lists are intentionally separate from /summary. Page 0 loads
  // after KPI summary arrives; loadMoreOverdue/loadMoreWarning page through the rest server-side.
  // Search/tier filtering is also server-side (setDetailFilter) so a filter searches every
  // matching customer, not just the pages already scrolled into memory.
  const overdueRows = shallowRef([])
  const overduePage = ref(-1)
  const overdueTotal = ref(0)
  const overdueHasMore = ref(false)
  const overdueLoadingMore = ref(false)

  const warningRows = shallowRef([])
  const warningPage = ref(-1)
  const warningTotal = ref(0)
  const warningHasMore = ref(false)
  const warningLoadingMore = ref(false)

  const detailSearch = ref('')
  const detailTier = ref('')
  // Bumped on every scope/dataset change so stale detail responses are discarded, and on every
  // filter change so a slow earlier query can't overwrite the current filter's rows.
  let detailVersion = 0

  // Search/filter state (used for server-side search when not all data is in memory)
  const filterSearch = ref('')
  const filterTier = ref('')
  const uploadMode = ref(false)  // true when all data came from upload (fully in memory)
  const loadedOnce = ref(false)
  const emptyDatasetLoaded = ref(false)

  // True when the full unfiltered dataset is already in memory — client-side filter is sufficient
  const isAllLoaded = computed(() =>
    uploadMode.value ||
    (filterSearch.value === '' && filterTier.value === '' &&
      loadedOnce.value && !hasMore.value && results.value.length >= totalCount.value)
  )

  let initPromise = null
  let requestVersion = 0
  const customerCache = new Map()

  async function init() {
    if (initPromise) return initPromise
    initPromise = _fetchInitial()
    return initPromise
  }

  async function _fetchInitial() {
    const version = nextRequestVersion()
    loading.value = true
    error.value = null
    uploadMode.value = false
    try {
      // Summary is non-fatal — the computed fallbacks below cover an unavailable summary,
      // so it is settled separately instead of rejecting the whole Promise.all.
      const [summarySettled, pageData] = await Promise.all([
        scoreApi.summary().then(d => ({ ok: true, d }), e => ({ ok: false, e })),
        scoreApi.results({ index: 0, size: PAGE_SIZE }, currentFilter()),
      ])
      if (!isCurrentRequest(version)) return
      rememberCustomerDetails(pageData.data)
      results.value = toListRows(pageData.data)
      currentPage.value = 0
      totalCount.value = pageData.page?.total ?? results.value.length
      hasMore.value = results.value.length < totalCount.value
      loadedOnce.value = true
      emptyDatasetLoaded.value = totalCount.value === 0
      if (summarySettled.ok) {
        if (!isCurrentRequest(version)) return
        summary.value = summarySettled.d
        resetDetailLists(summarySettled.d)
        loadInitialDetailLists()
      }
    } catch (e) {
      // A failed init must not stick. initPromise is what makes init() a no-op on later mounts, so
      // leaving a settled-but-failed one in place keeps every credit-score page empty for the rest
      // of the session — one expired token at page load would poison all of them.
      initPromise = null
      if (isCurrentRequest(version)) error.value = e.message
    } finally {
      if (isCurrentRequest(version)) loading.value = false
    }
  }

  /** The active results filter, in the shape the `{ page, filter }` envelope wants. */
  function currentFilter() {
    return { search: filterSearch.value || '', tier: filterTier.value || '' }
  }

  async function loadNextPage() {
    if (!hasMore.value || loadingMore.value) return false
    const version = requestVersion
    loadingMore.value = true
    try {
      const nextPage = currentPage.value + 1
      const pageData = await scoreApi.results({ index: nextPage, size: PAGE_SIZE }, currentFilter())
      if (!isCurrentRequest(version)) return false
      rememberCustomerDetails(pageData.data)
      totalCount.value = pageData.page?.total ?? totalCount.value
      results.value = [...results.value, ...toListRows(pageData.data)]
      currentPage.value = nextPage
      hasMore.value = results.value.length < totalCount.value
      loadedOnce.value = true
      return true
    } catch (e) {
      if (isCurrentRequest(version)) {
        error.value = e.message
        hasMore.value = false
      }
      return false
    } finally {
      loadingMore.value = false
    }
  }

  async function loadAll() {
    while (hasMore.value) {
      const loaded = await loadNextPage()
      if (!loaded) break
    }
  }

  // Apply search/tier filter — re-fetches from server page 0 with the new params.
  // No-op when uploadMode is true (all data is in memory; component handles client-side filtering).
  async function setFilter(search, tier) {
    const s = (search ?? '').trim()
    const t = tier ?? ''
    if (uploadMode.value) return

    if (emptyDatasetLoaded.value) {
      filterSearch.value = s
      filterTier.value = t
      results.value = []
      currentPage.value = 0
      totalCount.value = 0
      hasMore.value = false
      loadedOnce.value = true
      error.value = null
      return
    }

    const version = nextRequestVersion()
    filterSearch.value = s
    filterTier.value = t
    results.value = []
    currentPage.value = 0
    totalCount.value = 0
    hasMore.value = false
    loading.value = true
    error.value = null
    try {
      const pageData = await scoreApi.results({ index: 0, size: PAGE_SIZE }, { search: s, tier: t })
      if (!isCurrentRequest(version)) return
      rememberCustomerDetails(pageData.data)
      results.value = toListRows(pageData.data)
      currentPage.value = 0
      totalCount.value = pageData.page?.total ?? results.value.length
      hasMore.value = results.value.length < totalCount.value
      loadedOnce.value = true
      if (!s && !t) emptyDatasetLoaded.value = totalCount.value === 0
    } catch (e) {
      if (isCurrentRequest(version)) error.value = e.message
    } finally {
      if (isCurrentRequest(version)) loading.value = false
    }
  }

  async function fetchCustomer(id) {
    if (!id) return null
    const routeId = routeCustomerId({ cusUid: id, customerId: id, customerName: id })
    if (customerCache.has(routeId)) return customerCache.get(routeId)
    const fromStore = allKnownCustomers().find(r => sameCustomerRouteId(r, routeId))
    if (fromStore && hasCustomerDetails(fromStore)) return fromStore
    try {
      // cusUid moved from a path segment to a body field — no encodeURIComponent needed now.
      const customer = await scoreApi.result(routeId)
      rememberCustomerDetails([customer])
      return customer
    } catch (e) {
      if (fromStore) return fromStore
      // 404 is the only status that really means "this customer is not in the dataset". Every other
      // failure keeps its ScoreSvcError (401/403 on an expired session, 5xx, offline) so the views
      // can read `status` off it and name it — collapsing them all into `null` is what made an
      // expired session render as "ไม่พบข้อมูลลูกค้า".
      if (e?.status === 404) return null
      throw e
    }
  }

  async function _fetchSummaryOnly(version = requestVersion) {
    try {
      const summaryData = await scoreApi.summary()
      if (!isCurrentRequest(version)) return
      summary.value = summaryData
      resetDetailLists(summaryData)
      loadInitialDetailLists()
    } catch (_e) { /* non-fatal — summary fallback covers this */ }
  }

  // Clears both detail lists and invalidates any in-flight page load. Detail loads are versioned
  // on detailVersion (NOT requestVersion) so an unrelated results-filter change can't silently
  // discard the dashboard's page-0 load and leave the tables permanently empty.
  function clearDetailLists({ keepFilter = false, overdueCount = 0, warningCount = 0 } = {}) {
    detailVersion += 1
    if (!keepFilter) {
      detailSearch.value = ''
      detailTier.value = ''
    }
    overdueRows.value = []
    overduePage.value = -1
    overdueTotal.value = overdueCount
    overdueHasMore.value = overdueCount > 0
    overdueLoadingMore.value = false

    warningRows.value = []
    warningPage.value = -1
    warningTotal.value = warningCount
    warningHasMore.value = warningCount > 0
    warningLoadingMore.value = false
  }

  function resetDetailLists(summaryData) {
    clearDetailLists({
      overdueCount: summaryData?.overdueCustomerCount ?? 0,
      warningCount: summaryData?.earlyWarningCount ?? 0,
    })
  }

  function loadInitialDetailLists() {
    return Promise.all([
      _loadDetailPage('overdue', 0, true),
      _loadDetailPage('warning', 0, true),
    ])
  }

  // Self-heal for the dashboard: re-fires page 0 for any list that should have rows but doesn't
  // (e.g. its initial load was invalidated mid-flight). Safe to call on every mount.
  function ensureDetailListsLoaded() {
    const jobs = []
    if (overdueTotal.value > 0 && overdueRows.value.length === 0 && !overdueLoadingMore.value) {
      jobs.push(_loadDetailPage('overdue', 0, true))
    }
    if (warningTotal.value > 0 && warningRows.value.length === 0 && !warningLoadingMore.value) {
      jobs.push(_loadDetailPage('warning', 0, true))
    }
    return Promise.all(jobs)
  }

  // Server-side search/tier filter for the two detail lists — filters the whole matching set,
  // not just the pages already loaded. Re-fetches page 0 with the new params.
  function setDetailFilter(search, tier) {
    const s = (search ?? '').trim()
    const t = tier ?? ''
    if (s === detailSearch.value && t === detailTier.value) return Promise.resolve()
    clearDetailLists({
      keepFilter: true,
      overdueCount: overdueTotal.value,
      warningCount: warningTotal.value,
    })
    detailSearch.value = s
    detailTier.value = t
    return loadInitialDetailLists()
  }

  async function _loadDetailPage(kind, page, replace) {
    const isOverdue = kind === 'overdue'
    const rowsRef = isOverdue ? overdueRows : warningRows
    const pageRef = isOverdue ? overduePage : warningPage
    const totalRef = isOverdue ? overdueTotal : warningTotal
    const hasMoreRef = isOverdue ? overdueHasMore : warningHasMore
    const loadingRef = isOverdue ? overdueLoadingMore : warningLoadingMore
    const call = isOverdue ? scoreApi.overdue : scoreApi.warning

    if (loadingRef.value) return false
    if (!replace && !hasMoreRef.value) return false

    const version = detailVersion
    loadingRef.value = true
    try {
      const pageData = await call(
        { index: page, size: DETAIL_PAGE_SIZE },
        { search: detailSearch.value || '', tier: detailTier.value || '' }
      )
      if (version !== detailVersion) return false
      rememberCustomerDetails(pageData.data)
      totalRef.value = pageData.page?.total ?? totalRef.value
      rowsRef.value = replace ? toListRows(pageData.data) : [...rowsRef.value, ...toListRows(pageData.data)]
      pageRef.value = page
      hasMoreRef.value = rowsRef.value.length < totalRef.value
      return true
    } catch (e) {
      // Surface the failure instead of silently rendering an empty table.
      if (version === detailVersion) {
        error.value = e.message
        hasMoreRef.value = false
      }
      return false
    } finally {
      if (version === detailVersion) loadingRef.value = false
    }
  }

  function _loadMoreDetail(kind) {
    const pageRef = kind === 'overdue' ? overduePage : warningPage
    return _loadDetailPage(kind, pageRef.value + 1, false)
  }

  function loadMoreOverdue() { return _loadMoreDetail('overdue') }
  function loadMoreWarning() { return _loadMoreDetail('warning') }

  function saveResults(data) {
    nextRequestVersion()
    customerCache.clear()
    rememberCustomerDetails(data)
    results.value = toListRows(data)
    summary.value = null
    currentPage.value = 0
    totalCount.value = Array.isArray(data) ? data.length : 0
    hasMore.value = false
    filterSearch.value = ''
    filterTier.value = ''
    uploadMode.value = true  // all data is in memory from the upload response
    loadedOnce.value = true
    emptyDatasetLoaded.value = totalCount.value === 0
    clearDetailLists()
    initPromise = Promise.resolve()
    _fetchSummaryOnly(requestVersion)
  }

  async function refresh() {
    nextRequestVersion()
    initPromise = null
    results.value = []
    summary.value = null
    currentPage.value = 0
    totalCount.value = 0
    hasMore.value = false
    filterSearch.value = ''
    filterTier.value = ''
    uploadMode.value = false
    loadedOnce.value = false
    emptyDatasetLoaded.value = false
    loadingMore.value = false
    clearDetailLists()
    customerCache.clear()
    return init()
  }

  function clear() {
    nextRequestVersion()
    results.value = []
    summary.value = null
    currentPage.value = 0
    totalCount.value = 0
    hasMore.value = false
    filterSearch.value = ''
    filterTier.value = ''
    uploadMode.value = false
    loadedOnce.value = false
    emptyDatasetLoaded.value = false
    loading.value = false
    loadingMore.value = false
    initPromise = null
    clearDetailLists()
    customerCache.clear()
  }

  function clearFilterState() {
    if (uploadMode.value) return
    const hadFilter = filterSearch.value !== '' || filterTier.value !== ''
    filterSearch.value = ''
    filterTier.value = ''
    if (hadFilter) {
      nextRequestVersion()
      results.value = []
      totalCount.value = 0
      hasMore.value = false
      loadedOnce.value = false
      initPromise = null
    }
  }

  function nextRequestVersion() {
    requestVersion += 1
    return requestVersion
  }

  function isCurrentRequest(version) {
    return version === requestVersion
  }

  // Scope is now a body field rather than a query string, so the store just re-exports the
  // shared resolver. Still string-typed: groupUid/shopUid are Longs past MAX_SAFE_INTEGER.
  // Admin app: scope comes from shop_seleted (selected shop after switch), not from JWT.
  const scope = currentScope

  function routeCustomerId(row) {
    // cusUid first: the single-customer endpoint looks the customer up by cus_uid, so on any row
    // that carries both that is the field which resolves. The service normalises both to the same
    // value on score rows, so this only changes rows where the two actually differ.
    const id = row?.cusUid ?? row?.customerId ?? row?.customerName
    return id == null ? '' : String(id)
  }

  function sameCustomerRouteId(row, id) {
    const key = id == null ? '' : String(id)
    return [row?.cusUid, row?.customerId, row?.customerName]
      .some(v => v != null && String(v) === key)
  }

  function allKnownCustomers() {
    return [
      ...results.value,
      ...overdueRows.value,
      ...warningRows.value,
    ]
  }

  function rememberCustomerDetails(rows) {
    if (!Array.isArray(rows)) return
    for (const row of rows) {
      if (!hasCustomerDetails(row)) continue
      for (const key of customerKeys(row)) {
        customerCache.set(key, row)
      }
    }
  }

  function customerKeys(row) {
    return [row?.customerId, row?.cusUid, row?.customerName]
      .filter(v => v != null && String(v) !== '')
      .map(v => String(v))
  }

  // ── Computed (prefer summary, fall back to results for post-upload state) ──
  const hasData = computed(() =>
    (summary.value?.customerCount ?? 0) > 0 || results.value.length > 0
  )

  const totalCustomers = computed(() =>
    summary.value?.customerCount ?? results.value.length
  )

  const overdueCustomers = computed(() =>
    summary.value?.overdueCustomerCount ?? results.value.filter(r => r.overdueChains > 0).length
  )

  const earlyWarningCustomers = computed(() =>
    summary.value?.earlyWarningCount ?? results.value.filter(isEarlyWarningScore).length
  )

  const avgRisk = computed(() =>
    summary.value?.avgRisk ??
    (results.value.length ? results.value.reduce((s, r) => s + r.riskFinal, 0) / results.value.length : 0)
  )

  const avgValue = computed(() =>
    summary.value?.avgValue ??
    (results.value.length ? results.value.reduce((s, r) => s + r.valueFinal, 0) / results.value.length : 0)
  )

  const avgDmi = computed(() =>
    summary.value?.avgDmi ??
    (results.value.length ? results.value.reduce((s, r) => s + (r.dmi ?? r.dmiScore ?? 0), 0) / results.value.length : 0)
  )

  const totalOpenTickets = computed(() =>
    summary.value?.totalOpenTickets ?? results.value.reduce((s, r) => s + (r.openChains || 0), 0)
  )

  function _trendFromResults(field) {
    let posCount = 0, negCount = 0, zeroCount = 0, posSum = 0, negSum = 0
    for (const r of results.value) {
      const v = r[field] || 0
      if (v > 0) { posCount++; posSum += v }
      else if (v < 0) { negCount++; negSum += v }
      else zeroCount++
    }
    return {
      posCount, negCount, zeroCount,
      posAvg: posCount > 0 ? posSum / posCount : 0,
      negAvg: negCount > 0 ? negSum / negCount : 0,
    }
  }

  const riskTrend = computed(() =>
    summary.value?.riskTrend ?? _trendFromResults('riskTrendAdj')
  )
  const renewalTrend = computed(() =>
    summary.value?.renewalTrend ?? _trendFromResults('lateRenewalTrendAdj')
  )
  const earlyWarningTrend = computed(() =>
    summary.value?.earlyWarningTrend ?? _trendFromResults('earlyWarningAdj')
  )
  const valueTrend = computed(() =>
    summary.value?.valueTrend ?? _trendFromResults('valueTrendAdj')
  )

  const withOpenCount = computed(() =>
    summary.value?.withOpenCount ?? results.value.filter(r => (r.openChains || 0) > 0).length
  )

  const totalOverdueChains = computed(() =>
    summary.value?.totalOverdueChains ?? results.value.reduce((s, r) => s + (r.overdueChains || 0), 0)
  )

  const totalOverduePrincipal = computed(() =>
    summary.value?.totalOverduePrincipal ?? results.value.reduce((s, r) => s + (r.overduePrincipal || 0), 0)
  )

  const totalOpenPrincipal = computed(() =>
    summary.value?.totalOpenPrincipal ?? results.value.reduce((s, r) => s + (r.openPrincipal || 0), 0)
  )

  const maxDaysOverdue = computed(() =>
    summary.value?.maxDaysOverdue ?? results.value.reduce((m, r) => Math.max(m, r.maxDaysOverdue || 0), 0)
  )

  // Once a summary has arrived, overdueRows/warningRows (separately paged rows) are the
  // source of truth; before that, derive a best-effort list from whatever results are loaded.
  const overdueList = computed(() =>
    summary.value
      ? overdueRows.value
      : [...results.value.filter(r => r.overdueChains > 0)].sort((a, b) => b.maxDaysOverdue - a.maxDaysOverdue)
  )

  const warningList = computed(() =>
    summary.value
      ? warningRows.value
      : [...results.value.filter(isEarlyWarningScore)]
        .sort((a, b) => a.earlyWarningAdj - b.earlyWarningAdj || a.riskFinal - b.riskFinal)
  )

  const timestamp = computed(() => summary.value?.lastScoreDate ?? null)
  const uploadedAt = computed(() => summary.value?.lastScoreDate ?? null)

  return {
    results,
    summary,
    loading,
    loadingMore,
    error,
    selectedCustomerId,
    currentPage,
    totalCount,
    hasMore,
    isAllLoaded,
    uploadMode,
    timestamp,
    uploadedAt,
    init,
    refresh,
    loadNextPage,
    loadAll,
    setFilter,
    clearFilterState,
    fetchCustomer,
    saveResults,
    clear,
    scope,
    routeCustomerId,
    hasData,
    totalCustomers,
    overdueCustomers,
    earlyWarningCustomers,
    avgRisk,
    avgValue,
    avgDmi,
    totalOpenTickets,
    withOpenCount,
    riskTrend,
    renewalTrend,
    earlyWarningTrend,
    valueTrend,
    totalOverdueChains,
    totalOverduePrincipal,
    totalOpenPrincipal,
    maxDaysOverdue,
    overdueList,
    warningList,
    overdueHasMore,
    warningHasMore,
    overdueLoadingMore,
    warningLoadingMore,
    loadMoreOverdue,
    loadMoreWarning,
    overdueTotal,
    warningTotal,
    ensureDetailListsLoaded,
    setDetailFilter,
  }
})


function toListRows(data) {
  return Array.isArray(data) ? data.map(toListRow) : []
}

function toListRow(row) {
  if (!row || !Object.prototype.hasOwnProperty.call(row, 'openTickets')) return row
  // eslint-disable-next-line no-unused-vars
  const { openTickets, ...listRow } = row
  return listRow
}

// A row counts as "detailed" only when its openTickets can be trusted as data. The list endpoints
// /score/overdue and /score/warning ship `openTickets: []` as a placeholder (the service maps them
// without open tickets), so key-presence alone would cache a ticket-less row and make
// fetchCustomer() serve it instead of calling /score/results — CsPortfolio would then show the
// customer with an empty open-ticket table. An empty array is only real when the customer has no
// open chains at all.
function hasCustomerDetails(row) {
  if (!row || !Object.prototype.hasOwnProperty.call(row, 'openTickets')) return false
  if (!Array.isArray(row.openTickets)) return true
  return row.openTickets.length > 0 || (row.openChains ?? 0) === 0
}
